/**
 * HowItWorks — Landing, Códice Botánico edition.
 * 3-step process. SVG icons (no emoji), codex palette, Fraunces headings.
 */
import React from 'react';
import { motion } from 'framer-motion';

const steps = [
  {
    number: '1',
    title: 'Crea tu cuenta',
    description: 'Gratis, en 2 minutos',
    details: 'Elige tu objetivo: digestión, energía, peso o salud general.',
    color: 'var(--cdx-jade)',
    Icon: SeedIcon,
  },
  {
    number: '2',
    title: 'Usa las herramientas',
    description: 'Todo el herbario a tu alcance',
    details: 'Consulta plantas, arma tu plan semanal, pregunta a Huitzi.',
    color: 'var(--cdx-turquesa)',
    Icon: MortarIcon,
  },
  {
    number: '3',
    title: 'Cuida tu progreso',
    description: 'Ajusta y florece',
    details: 'Registra cómo te sientes y afina tu plan con criterio.',
    color: 'var(--cdx-cempasuchil)',
    Icon: BloomIcon,
  },
];

const stats = [
  ['56+', 'Alimentos SMAE'],
  ['22', 'Plantas medicinales'],
  ['16', 'Técnicas culinarias'],
  ['100%', 'Gratis'],
  ['<2s', 'Respuesta de Huitzi'],
];

const HowItWorks = () => (
  <section
    className="cdx-grain relative"
    style={{ padding: '88px 0', background: 'var(--cdx-paper)', fontFamily: 'var(--cdx-font-body)' }}
  >
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-[1]">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="text-center mb-16"
      >
        <span className="cdx-kicker">El camino</span>
        <h2 className="cdx-display" style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', margin: '10px 0 12px', color: 'var(--cdx-ink)' }}>
          Tan simple como <em style={{ color: 'var(--cdx-jade)', fontStyle: 'italic' }}>1, 2, 3</em>
        </h2>
        <p style={{ fontSize: 18, color: 'var(--cdx-ink-soft)', maxWidth: 540, margin: '0 auto' }}>
          Empieza tu camino hacia una mejor salud en minutos.
        </p>
      </motion.div>

      <div className="grid md:grid-cols-3 gap-8 relative">
        {steps.map((step, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.15, duration: 0.6 }}
          >
            <div
              className="cdx-grain"
              style={{
                position: 'relative',
                background: 'var(--cdx-paper-2)',
                border: '1px solid var(--cdx-paper-edge)',
                borderRadius: 'var(--cdx-r-lg)',
                padding: '0 0 26px',
                boxShadow: 'var(--cdx-shadow-sm)',
                overflow: 'hidden',
                height: '100%',
              }}
            >
              <div className="cdx-greca" style={{ position: 'relative', zIndex: 1 }} />
              <div style={{ padding: '24px 22px 0', textAlign: 'center', position: 'relative', zIndex: 1 }}>
                <div
                  style={{
                    width: 64, height: 64, borderRadius: '50%', margin: '0 auto 16px',
                    background: step.color, color: 'var(--cdx-nighttext)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: 'var(--cdx-shadow-md)',
                  }}
                >
                  <step.Icon />
                </div>
                <span className="cdx-kicker" style={{ color: step.color }}>Paso {step.number}</span>
                <h3 className="cdx-display" style={{ fontSize: 21, color: 'var(--cdx-ink)', margin: '6px 0 6px' }}>
                  {step.title}
                </h3>
                <p style={{ color: 'var(--cdx-jade)', fontWeight: 600, fontSize: 14.5, marginBottom: 8 }}>
                  {step.description}
                </p>
                <p style={{ fontSize: 13.5, color: 'var(--cdx-ink-faint)', lineHeight: 1.5 }}>
                  {step.details}
                </p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="mt-16 text-center"
      >
        <a href="/auth/register" className="cdx-btn cdx-btn--primary" style={{ padding: '15px 32px', fontSize: 17 }}>
          Empezar ahora — es gratis
        </a>
        <p style={{ marginTop: 14, fontSize: 13.5, color: 'var(--cdx-ink-faint)' }}>
          No se requiere tarjeta de crédito
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="mt-20 grid grid-cols-2 md:grid-cols-5 gap-6"
      >
        {stats.map(([num, label]) => (
          <div key={label} className="text-center">
            <div className="cdx-display" style={{ fontSize: 30, fontWeight: 600, color: 'var(--cdx-jade)', marginBottom: 4 }}>{num}</div>
            <div style={{ fontSize: 13, color: 'var(--cdx-ink-soft)' }}>{label}</div>
          </div>
        ))}
      </motion.div>
    </div>
  </section>
);

/* --- step SVG icons (cream stroke, sit on colored discs) --- */
function SeedIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden>
      <path d="M12 21c0-6-3-10-7-11 0 6 3 10 7 11Z" fill="currentColor" opacity="0.25" />
      <path d="M12 21c0-6 3-10 7-11 0 6-3 10-7 11Z" fill="currentColor" opacity="0.5" />
      <path d="M12 21V8" strokeLinecap="round" />
    </svg>
  );
}
function MortarIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden>
      <path d="M5 11h14a7 7 0 0 1-7 7 7 7 0 0 1-7-7Z" strokeLinejoin="round" />
      <path d="M12 18v3M8 21h8" strokeLinecap="round" />
      <path d="M14 4l4 4" strokeLinecap="round" />
    </svg>
  );
}
function BloomIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden>
      <circle cx="12" cy="9" r="2.4" fill="currentColor" />
      {[0, 72, 144, 216, 288].map((d) => (
        <ellipse key={d} cx="12" cy="4.6" rx="1.7" ry="3" fill="currentColor" opacity="0.55" transform={`rotate(${d} 12 9)`} />
      ))}
      <path d="M12 12v9" strokeLinecap="round" />
    </svg>
  );
}

export default HowItWorks;
